import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, TextField, MenuItem, Button, IconButton } from '@mui/material';
import { Add, Close } from '@mui/icons-material';
import { Star, ListChecks } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { ICompany } from '../../../redux/company/types';
import { SERVICE } from '../../../data/forms/company.data';
import { updateCompany } from '../../../redux/company/actions';
import { COMPANY_BRAND_ORANGE } from '../../../constants/companyBrand';

interface ICompanyServicesEdit {
    company?: ICompany;
    isEditing: boolean;
    onSave: (saveFunction: () => void) => void;
    onChanges: (hasChanges: boolean) => void;
}

const SURFACE_CARD = '#1a1a1a';
const INPUT_BLACK = '#000000';
const BTN_ADD_BG = '#262626';
const LABEL_MUTED = 'rgba(255,255,255,0.45)';
const MAX_ADDITIONAL_SERVICES = 8;

const selectFieldSx = {
    '& .MuiOutlinedInput-root': {
        backgroundColor: INPUT_BLACK,
        color: '#ffffff',
        borderRadius: 2,
        fontFamily: FONT_ENUM.MAIN_REGULAR,
        '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.22)' },
        '&.Mui-focused fieldset': { borderColor: COMPANY_BRAND_ORANGE },
    },
    '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.7)' },
};

const CompanyServicesEdit: React.FC<ICompanyServicesEdit> = ({ company, isEditing, onSave, onChanges }) => {
    const dispatch = useDispatch();
    const [formData, setFormData] = useState({
        service: company?.service || 'Media services',
        other_services: company?.other_services || [],
    });
    const [initialData, setInitialData] = useState(formData);
    const [newService, setNewService] = useState('');

    useEffect(() => {
        if (company) {
            const newData = {
                service: company.service || 'Media services',
                other_services: company.other_services || [],
            };
            setFormData(newData);
            setInitialData(newData);
        }
    }, [company]);

    useEffect(() => {
        const hasChanges = JSON.stringify(formData) !== JSON.stringify(initialData);
        onChanges(hasChanges);
    }, [formData, initialData, onChanges]);

    const handleFieldChange = (field: string, value: unknown) => {
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    const addService = () => {
        if (
            newService.trim() &&
            !formData.other_services.includes(newService.trim()) &&
            formData.other_services.length < MAX_ADDITIONAL_SERVICES
        ) {
            handleFieldChange('other_services', [...formData.other_services, newService.trim()]);
            setNewService('');
        }
    };

    const removeService = (serviceToRemove: string) => {
        handleFieldChange(
            'other_services',
            formData.other_services.filter(service => service !== serviceToRemove),
        );
    };

    const handleSave = () => {
        if (company?._id) {
            dispatch(
                updateCompany({
                id: company._id,
                body: {
                    service: formData.service,
                    other_services: formData.other_services,
                },
                }),
            );
        }
    };

    useEffect(() => {
        onSave(handleSave);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onSave, formData, company?._id]);

    const spotsLabel = `${formData.other_services.length} of ${MAX_ADDITIONAL_SERVICES} spots used`;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
                <Typography
                    component="h1"
                    sx={{
                        fontFamily: FONT_ENUM.BOLD_HEADER,
                        fontWeight: 800,
                        fontSize: { xs: '1.75rem', md: '2.25rem' },
                        color: '#ffffff',
                        letterSpacing: '-0.02em',
                        mb: 1.5,
                    }}>
                    Services &{' '}
                    <Box component="span" sx={{ color: COMPANY_BRAND_ORANGE }}>
                        Industry
                    </Box>
                </Typography>
                <Typography
                    sx={{
                        color: 'rgba(255,255,255,0.55)',
                        maxWidth: 560,
                        lineHeight: 1.65,
                        fontSize: '0.95rem',
                        fontFamily: FONT_ENUM.MEDIUM_TEXT,
                    }}>
                    Define your professional reach and core expertise.
                </Typography>
            </Box>

            <Box
                sx={{
                    bgcolor: SURFACE_CARD,
                    borderRadius: 3,
                    p: { xs: 2, sm: 3 },
                    border: '1px solid rgba(255,255,255,0.06)',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
                    borderLeft: `4px solid ${COMPANY_BRAND_ORANGE}`,
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                    <Star size={22} color={COMPANY_BRAND_ORANGE} strokeWidth={2} />
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.BOLD_HEADER,
                            fontWeight: 700,
                            fontSize: '1.125rem',
                            color: '#ffffff',
                        }}>
                        Primary Industry Focus
                    </Typography>
                </Box>
                        <Typography
                            sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: '0.14em',
                        textTransform: 'uppercase',
                        color: LABEL_MUTED,
                        mb: 1,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                            }}>
                    Core category
                        </Typography>
                        {isEditing ? (
                    <TextField
                        select
                        fullWidth
                                    value={formData.service}
                                    onChange={e => handleFieldChange('service', e.target.value)}
                        sx={selectFieldSx}
                        SelectProps={{
                            MenuProps: {
                                        PaperProps: {
                                            sx: {
                                        bgcolor: '#1a1a1a',
                                        border: '1px solid rgba(255,255,255,0.1)',
                                                '& .MuiMenuItem-root': {
                                            color: '#fff',
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                                                    '&.Mui-selected': {
                                                bgcolor: `${COMPANY_BRAND_ORANGE}2a`,
                                                color: COMPANY_BRAND_ORANGE,
                                                        },
                                                    },
                                                },
                                            },
                                        },
                                    }}>
                                    {SERVICE.map(service => (
                                        <MenuItem key={service} value={service}>
                                            {service}
                                        </MenuItem>
                                    ))}
                    </TextField>
                        ) : (
                    <Box
                                sx={{
                            bgcolor: INPUT_BLACK,
                            borderRadius: 2,
                            px: 2,
                            py: 1.75,
                            border: '1px solid rgba(255,255,255,0.08)',
                            color: '#ffffff',
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        }}>
                        {formData.service}
                    </Box>
                )}
            </Box>

            <Box
                sx={{
                    bgcolor: SURFACE_CARD,
                    borderRadius: 3,
                    p: { xs: 2, sm: 3 },
                    border: '1px solid rgba(255,255,255,0.06)',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
                }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <ListChecks size={22} color={COMPANY_BRAND_ORANGE} strokeWidth={2} />
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.BOLD_HEADER,
                                fontWeight: 700,
                                fontSize: '1.125rem',
                                color: '#ffffff',
                            }}>
                            Additional Services
                        </Typography>
                    </Box>
                    <Typography
                                    sx={{
                            fontSize: 11,
                            color: LABEL_MUTED,
                            fontFamily: FONT_ENUM.MEDIUM_TEXT,
                        }}>
                        {spotsLabel}
                    </Typography>
                        </Box>

                        {isEditing && (
                    <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                                <TextField
                                    value={newService}
                                    onChange={e => setNewService(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && addService()}
                            placeholder="e.g., Color Grading, Script Doctoring..."
                                    fullWidth
                                    sx={{
                                flex: '1 1 200px',
                                        '& .MuiOutlinedInput-root': {
                                    backgroundColor: INPUT_BLACK,
                                    color: '#ffffff',
                                    borderRadius: 2,
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.22)' },
                                    '&.Mui-focused fieldset': { borderColor: COMPANY_BRAND_ORANGE },
                                },
                                '& .MuiInputBase-input::placeholder': {
                                    color: 'rgba(255,255,255,0.35)',
                                    opacity: 1,
                                },
                            }}
                            disabled={formData.other_services.length >= MAX_ADDITIONAL_SERVICES}
                        />
                        <Button
                            variant="contained"
                            disableElevation
                            onClick={addService}
                            disabled={!newService.trim() || formData.other_services.length >= MAX_ADDITIONAL_SERVICES}
                            startIcon={<Add />}
                            sx={{
                                bgcolor: BTN_ADD_BG,
                                color: '#ffffff',
                                textTransform: 'none',
                                fontWeight: 700,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                px: 2.5,
                                borderRadius: 2,
                                '&:hover': { bgcolor: '#333333' },
                                '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.35)' },
                            }}>
                            Add
                        </Button>
                    </Box>
                )}

                {formData.other_services.length > 0 ? (
                    <Grid container spacing={1.5}>
                        {formData.other_services.map(service => (
                            <Grid item xs={12} sm={6} key={service}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        bgcolor: '#131313',
                                        borderRadius: 2,
                                        px: 2,
                                        py: 1.25,
                                        border: '1px solid rgba(255,255,255,0.08)',
                                    }}>
                                    <Box
                                        sx={{
                                            width: 6,
                                            height: 6,
                                            borderRadius: '50%',
                                            bgcolor: COMPANY_BRAND_ORANGE,
                                            flexShrink: 0,
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            flex: 1,
                                            color: '#ffffff',
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            fontSize: 14,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}>
                                        {service}
                                    </Typography>
                                    {isEditing && (
                                        <IconButton
                                            size="small"
                                            aria-label={`Remove ${service}`}
                                            onClick={() => removeService(service)}
                                            sx={{
                                                color: 'rgba(255,255,255,0.5)',
                                                '&:hover': { color: '#ff7351', bgcolor: 'rgba(255,115,81,0.12)' },
                                            }}>
                                            <Close sx={{ fontSize: 18 }} />
                                </IconButton>
                                    )}
                            </Box>
                            </Grid>
                        ))}
                    </Grid>
                ) : (
                    !isEditing && (
                            <Typography
                                sx={{
                                color: 'rgba(255,255,255,0.45)',
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontStyle: 'italic',
                                fontSize: 14,
                                }}>
                                No additional services specified
                            </Typography>
                    )
                )}
            </Box>
        </Box>
    );
};

export { CompanyServicesEdit };
